import {
  collection,
  arrayUnion,
  deleteDoc,
  updateDoc,
  where,
  query,
  doc,
  setDoc,
  getDoc,
  writeBatch,
  getDocs,
  onSnapshot,
  orderBy,
} from "firebase/firestore"
import { generateFirestoreId } from "../utils/nanoidUtils"
import {
  createDefaultNoteTabs,
  createDefaultSettings,
} from "../utils/generateNewUser"
import { firestore, auth } from "./firebase"
import sortOrder from "lodash/orderBy"
import { computeComparison } from "../utils/assessmentCompare"
import { POST_STATUS_RETESTED } from "../constants/constants"

export const firestoreGetClientPreviousNotes = async (clientId) => {
  const { uid } = auth.currentUser
  const notesCollection = collection(
    firestore,
    `users/${uid}/userTreatmentNotes`
  )

  const clientQuery = query(notesCollection, where("clientId", "==", clientId))
  return await getDocs(clientQuery)
}

export const firestoreGetAllUserTreatmentNotes = async () => {
  const uid = auth.currentUser?.uid
  if (!uid) return []

  const notesCollection = collection(firestore, `users/${uid}/userTreatmentNotes`)
  const notesSnapshot = await getDocs(notesCollection)

  return notesSnapshot.docs.map((docSnapshot) => ({
    docId: docSnapshot.id,
    ...docSnapshot.data(),
  }))
}

export const firestoreGetSavedTreatmentNoteImages = async (sessionId) => {
  const { uid } = auth.currentUser
  const imageCollection = collection(
    firestore,
    `users/${uid}/userTreatmentNotes/${sessionId}/images`
  )

  const imageDocs = await getDocs(imageCollection)
  const isImagesEmpty = imageDocs.empty

  if (isImagesEmpty) {
    return null
  } else {
    let data = []
    imageDocs.docs.forEach((doc) => data.push({ docId: doc.id, ...doc.data() }))

    return data
  }
}

export const firestoreGetClientDoc = async (clientId) => {
  const { uid } = auth.currentUser
  const newClientRef = doc(firestore, `users/${uid}/userClients/${clientId}`)
  return await getDoc(newClientRef)
}

export const firestoreSetNewClientDoc = async ({
  firstName,
  lastName,
  modelSex,
}) => {
  const { uid } = auth.currentUser
  const docId = generateFirestoreId()
  const clientId = docId
  const newClientRef = doc(firestore, `users/${uid}/userClients/${clientId}`)
  const client = {
    firstName,
    lastName,
    name: `${firstName.length > 0 ? firstName : ""}${
      lastName.length > 0 ? ` ${lastName}` : ""
    }`,
    modelSex,
    createdAt: new Date(),
    uid: generateFirestoreId(),
    key: generateFirestoreId(),
    treatmentHistoryIds: [],
    docId,
    clientId,
  }
  try {
    await setDoc(newClientRef, client)
    return docId
  } catch (error) {
    console.log(error)
    return null
  }
}

export const firestoreUpdateDoc = async ({
  collectionName,
  docId,
  docData,
}) => {
  const { uid } = auth.currentUser
  const path = `users/${uid}/${collectionName}/${docId}`
  const ref = doc(firestore, path)
  return await updateDoc(ref, { ...docData }, { merge: true })
}

export const firestoreDeleteDoc = async ({ collectionName, docId }) => {
  const { uid } = auth.currentUser
  const path = `users/${uid}/${collectionName}/${docId}`
  const ref = doc(firestore, path)
  return deleteDoc(ref)
}

export const firestoreCheckAllowedEmailList = async ({ registerEmail }) => {
  const ref = doc(firestore, "allowedEmails/allowedEmails")
  const emails = await getDoc(ref)
  return emails.data().emails.includes(registerEmail)
}
export const firestoreCheckUserDocExists = async (uid) => {
  const userRef = doc(firestore, `users/${uid}`)
  const userDoc = await getDoc(userRef)
  return userDoc.exists()
}

export const firestoreCreateNewUser = async ({ uid, email }) => {
  if (!uid) return
  const noteSettingsArray = createDefaultNoteTabs()
  const userSettingsArray = createDefaultSettings()
  const userDocRef = doc(firestore, `users/${uid}`)

  const userSettingsCollectionRef = collection(
    firestore,
    `users/${uid}/userSettings`
  )
  const userNoteSettingsCollectionRef = collection(
    firestore,
    `users/${uid}/userNoteSettings`
  )
  const batch = writeBatch(firestore)
  try {
    await setDoc(userDocRef, {
      uid,
      email,
      createdAt: new Date(),
    })
  } catch (error) {
    console.log(error)
  }

  try {
    // Generate New User Settings
    noteSettingsArray.forEach((n) => {
      const docId = n?.docId ?? generateFirestoreId()
      const ref = doc(userNoteSettingsCollectionRef, docId)
      batch.set(ref, n)
    })
    userSettingsArray.forEach((s) => {
      const docId = s?.docId ?? generateFirestoreId()
      const ref = doc(userSettingsCollectionRef, docId)
      batch.set(ref, s)
    })
    return await batch.commit()
  } catch (error) {
    console.log(error)
  }
}

const imageDelete = async (imageDocs, imageCollection) => {
  await imageDocs.docs.forEach(async (d) => {
    const docRef = doc(imageCollection, d.id)
    await deleteDoc(docRef)
  })
}

export const firestoreSaveSessionToFirestore = async ({ images, docData }) => {
  const { uid } = auth.currentUser
  const sessionId = docData.sessionId
  const clientId = docData?.clientId ?? null

  const treatmentNotesRef = doc(
    firestore,
    `users/${uid}/userTreatmentNotes/${sessionId}`
  )
  try {
    //TODO: make use of firebase storage
    const imageCollection = collection(
      firestore,
      `users/${uid}/userTreatmentNotes/${sessionId}/images`
    )

    const imageDocs = await getDocs(imageCollection)
    const isImagesEmpty = imageDocs.empty
    if (!isImagesEmpty) {
      await imageDelete(imageDocs, imageCollection)
    }
    if (images.length > 0) {
      const imagesRef = collection(
        firestore,
        `users/${uid}/userTreatmentNotes/${sessionId}/images`
      )
      const batch = writeBatch(firestore)
      await images.forEach((d) => {
        const docId = generateFirestoreId()
        const docRef = doc(imagesRef, docId)
        batch.set(docRef, d)
      })
      await batch.commit()
    }
  } catch (error) {
    console.log(error)
  } finally {
    if (clientId) {
      const clientDoc = doc(firestore, `users/${uid}/userClients/${clientId}`)
      try {
        await updateDoc(clientDoc, {
          treatmentHistoryIds: arrayUnion(sessionId),
        })
      } catch (error) {
        console.log(error)
      }
    }
    try {
      return await setDoc(
        treatmentNotesRef,
        {
          ...docData,
          lastModified: new Date(),
        },
        { merge: true }
      )
    } catch (error) {
      console.error(error.message, Object.keys(docData))
      throw error
    }
  }
}
// export const batchWriteImages = async ({ images, imageCollection }) => {
//   const batch = writeBatch(firestore)
//   images.forEach((s) => {
//     const ref = doc(imageCollection, s.id)
//     batch.set(ref, s)
//   })
//   return await batch.commit()
// }

export const firestoreAddNewUserSetting = async ({ settingType, docData }) => {
  const { uid } = auth.currentUser
  const userSettingsCollectionRef = collection(
    firestore,
    `users/${uid}/userSettings`
  )
  const key = generateFirestoreId()
  const id = generateFirestoreId()
  const docId = generateFirestoreId()
  const newDocRef = doc(userSettingsCollectionRef, docId)
  return await setDoc(newDocRef, {
    settingType,
    key,
    id,
    docId,
    createdAt: new Date(),
    ...docData,
  })
}

export const firestoreGetPreferredWebsiteDomainsSetting = async () => {
  const { uid } = auth.currentUser
  const userSettingsCollectionRef = collection(
    firestore,
    `users/${uid}/userSettings`
  )
  const settingsQuery = query(
    userSettingsCollectionRef,
    where("settingType", "==", "preferredWebsiteDomains")
  )
  const snapshot = await getDocs(settingsQuery)
  if (snapshot.empty) return null

  const firstDoc = snapshot.docs[0]
  return { docId: firstDoc.id, ...firstDoc.data() }
}

export const firestoreSavePreferredWebsiteDomainsSetting = async ({ domains }) => {
  const { uid } = auth.currentUser
  const userSettingsCollectionRef = collection(
    firestore,
    `users/${uid}/userSettings`
  )
  const settingsQuery = query(
    userSettingsCollectionRef,
    where("settingType", "==", "preferredWebsiteDomains")
  )
  const snapshot = await getDocs(settingsQuery)
  const safeDomains = Array.isArray(domains) ? domains : []

  if (snapshot.empty) {
    const docId = generateFirestoreId()
    const newDocRef = doc(userSettingsCollectionRef, docId)
    return await setDoc(newDocRef, {
      settingType: "preferredWebsiteDomains",
      key: generateFirestoreId(),
      id: generateFirestoreId(),
      docId,
      domains: safeDomains,
      createdAt: new Date(),
      updatedAt: new Date(),
    })
  }

  const firstDoc = snapshot.docs[0]
  const existingDocRef = doc(firestore, `users/${uid}/userSettings/${firstDoc.id}`)
  return await setDoc(
    existingDocRef,
    {
      domains: safeDomains,
      updatedAt: new Date(),
    },
    { merge: true }
  )
}

export const firestoreGetShowRedFlagButtonSetting = async () => {
  const { uid } = auth.currentUser
  const userSettingsCollectionRef = collection(
    firestore,
    `users/${uid}/userSettings`
  )
  const settingsQuery = query(
    userSettingsCollectionRef,
    where("settingType", "==", "showRedFlagButton")
  )
  const snapshot = await getDocs(settingsQuery)
  if (snapshot.empty) return null

  const firstDoc = snapshot.docs[0]
  return { docId: firstDoc.id, ...firstDoc.data() }
}

export const firestoreSaveShowRedFlagButtonSetting = async ({ showRedFlagButton }) => {
  const { uid } = auth.currentUser
  const userSettingsCollectionRef = collection(
    firestore,
    `users/${uid}/userSettings`
  )
  const settingsQuery = query(
    userSettingsCollectionRef,
    where("settingType", "==", "showRedFlagButton")
  )
  const snapshot = await getDocs(settingsQuery)
  const safeValue = Boolean(showRedFlagButton)

  if (snapshot.empty) {
    const docId = generateFirestoreId()
    const newDocRef = doc(userSettingsCollectionRef, docId)
    return await setDoc(newDocRef, {
      settingType: "showRedFlagButton",
      key: generateFirestoreId(),
      id: generateFirestoreId(),
      docId,
      showRedFlagButton: safeValue,
      createdAt: new Date(),
      updatedAt: new Date(),
    })
  }

  const firstDoc = snapshot.docs[0]
  const existingDocRef = doc(firestore, `users/${uid}/userSettings/${firstDoc.id}`)
  return await setDoc(
    existingDocRef,
    {
      showRedFlagButton: safeValue,
      updatedAt: new Date(),
    },
    { merge: true }
  )
}

// ── Note Tab Settings (userNoteSettings collection) ─────────────────────────
export const firestoreAddNoteTab = async (docData) => {
  const { uid } = auth.currentUser
  const colRef = collection(firestore, `users/${uid}/userNoteSettings`)
  const docId = generateFirestoreId()
  const newDocRef = doc(colRef, docId)
  return await setDoc(newDocRef, {
    key: generateFirestoreId(),
    id: generateFirestoreId(),
    docId,
    createdAt: new Date(),
    ...docData,
  })
}

export const firestoreDeleteNoteTab = async (docId) => {
  const { uid } = auth.currentUser
  const noteTabDoc = doc(firestore, `users/${uid}/userNoteSettings/${docId}`)
  return await deleteDoc(noteTabDoc)
}

export const firestoreUpdateNoteTab = async ({ docId, docData }) => {
  const { uid } = auth.currentUser
  const noteTabDoc = doc(firestore, `users/${uid}/userNoteSettings/${docId}`)
  return await updateDoc(noteTabDoc, { ...docData }, { merge: true })
}

export const firestoreUpdateUserSetting = async ({ docId, docData }) => {
  const { uid } = auth.currentUser
  const userSettingsDoc = doc(firestore, `users/${uid}/userSettings/${docId}`)

  return await updateDoc(userSettingsDoc, { ...docData }, { merge: true })
}

export const firestoreDeleteUserSetting = async (docId) => {
  const { uid } = auth.currentUser
  const userSettingsDoc = doc(firestore, `users/${uid}/userSettings/${docId}`)
  return await deleteDoc(userSettingsDoc)
}

// ── Technique Library (userTechniques collection) ───────────────────────────
export const firestoreAddTechnique = async ({ label, output, eventMode, showInSummary = true }) => {
  const { uid } = auth.currentUser
  const userTechniquesCollectionRef = collection(
    firestore,
    `users/${uid}/userTechniques`
  )
  const docId = generateFirestoreId()
  const newDocRef = doc(userTechniquesCollectionRef, docId)
  return await setDoc(newDocRef, {
    docId,
    label,
    output,
    eventMode: eventMode ?? null,
    areas: [],
    showInSummary,
    createdAt: new Date(),
  })
}

export const firestoreUpdateTechnique = async (docId, updates) => {
  const { uid } = auth.currentUser
  const techniqueDoc = doc(firestore, `users/${uid}/userTechniques/${docId}`)
  return await updateDoc(techniqueDoc, { ...updates }, { merge: true })
}

export const firestoreDeleteTechnique = async (docId) => {
  const { uid } = auth.currentUser
  const techniqueDoc = doc(firestore, `users/${uid}/userTechniques/${docId}`)
  return await deleteDoc(techniqueDoc)
}

export const firestoreGetImagesFromTreatment = async (sessionId) => {
  const { uid } = auth.currentUser
  const imagesRef = collection(
    firestore,
    `users/${uid}/userTreatmentNotes/${sessionId}/images`
  )
  return await getDocs(imagesRef)
}

/* User Settings onSnapshot */

export const userNoteSettingsOnSnapshot = ({ uid, setUserNoteSettings }) => {
  return onSnapshot(
    collection(firestore, `users/${uid}/userNoteSettings`),
    (query) => {
      let data = []
      query.docs.forEach((doc) => data.push({ ...doc.data() }))
      setUserNoteSettings(sortOrder(data, "orderIndex", "asc"))
    },
    (error) => {
      console.log(error)
    }
  )
}

export const userSettingsOnSnapshot = ({ uid, setUserSettings }) => {
  return onSnapshot(
    collection(firestore, `users/${uid}/userSettings`),
    (query) => {
      let data = []
      query.docs.forEach((doc) => data.push({ ...doc.data() }))
      setUserSettings(data)
    },
    (error) => {
      console.log(error)
    }
  )
}

export const userTechniquesOnSnapshot = ({ uid, setUserTechniques }) => {
  return onSnapshot(
    collection(firestore, `users/${uid}/userTechniques`),
    (query) => {
      let data = []
      query.docs.forEach((doc) => data.push({ ...doc.data() }))
      setUserTechniques(data)
    },
    (error) => {
      console.log(error)
    }
  )
}

export const userClientsOnSnapshot = ({ uid, setUserClients }) => {
  return onSnapshot(
    query(collection(firestore, `users/${uid}/userClients`), orderBy("name")),
    (query) => {
      let data = []
      query.docs.forEach((doc) => data.push({ ...doc.data() }))
      setUserClients(data)
    },
    (error) => {
      console.log(error)
    }
  )
}

/* ── Pre/Post Assessment Comparison ─────────────────────────────────────── */

const assessmentsCollectionRef = (uid, sessionId) =>
  collection(
    firestore,
    `users/${uid}/userTreatmentNotes/${sessionId}/assessments`
  )

const assessmentDocRef = (uid, sessionId, assessmentId) =>
  doc(
    firestore,
    `users/${uid}/userTreatmentNotes/${sessionId}/assessments/${assessmentId}`
  )

const clientOutcomeDocRef = (uid, clientId, testKey) =>
  doc(
    firestore,
    `users/${uid}/userClients/${clientId}/clientOutcomes/${testKey}`
  )

const testAliasDocRef = (uid, slug) =>
  doc(firestore, `users/${uid}/userTestAliases/${slug}`)

const sessionDocRef = (uid, sessionId) =>
  doc(firestore, `users/${uid}/userTreatmentNotes/${sessionId}`)

// Build revision entries only for user-driven edits (option b). System recomputes
// (e.g. comparison rebuilding when post changes) do not produce revisions.
const buildUserRevisions = (prev, next, editedBy) => {
  if (editedBy !== "user") return []
  const now = new Date()
  return Object.keys(next).flatMap((field) => {
    const prevValue = prev?.[field]
    const newValue = next[field]
    if (JSON.stringify(prevValue) === JSON.stringify(newValue)) return []
    return [{ editedAt: now, field, prevValue: prevValue ?? null, newValue }]
  })
}

export const firestoreCreatePreAssessment = async ({
  sessionId,
  clientId,
  testKey,
  testLabel,
  conditionName = null,
  source,
  measurementType,
  pre,
  markedForRetest = false,
  aliasResolved = true,
}) => {
  const { uid } = auth.currentUser
  const assessmentId = generateFirestoreId()
  const now = new Date()
  const docData = {
    assessmentId,
    sessionId,
    clientId: clientId ?? null,
    testKey,
    testLabel,
    conditionName,
    source,
    measurementType,
    pre: { ...pre, recordedAt: pre?.recordedAt ?? now },
    post: null,
    comparison: null,
    markedForRetest,
    aliasResolved,
    isDraft: true,
    revisions: [],
    createdAt: now,
    updatedAt: now,
  }
  await setDoc(assessmentDocRef(uid, sessionId, assessmentId), docData)
  await updateDoc(sessionDocRef(uid, sessionId), {
    hasPreAssessment: true,
  }).catch(() => {
    // Session doc may not exist yet for quick sessions — safe to ignore.
  })
  return docData
}

export const firestoreUpdatePreAssessment = async ({
  sessionId,
  assessmentId,
  patch,
  editedBy = "system",
}) => {
  const { uid } = auth.currentUser
  const ref = assessmentDocRef(uid, sessionId, assessmentId)
  const snap = await getDoc(ref)
  if (!snap.exists()) return null
  const current = snap.data()
  const nextPre = {
    ...current.pre,
    ...patch,
    recordedAt: current.pre?.recordedAt ?? new Date(),
  }
  const newRevisions = buildUserRevisions(
    { pre: current.pre },
    { pre: nextPre },
    editedBy
  )
  await updateDoc(ref, {
    pre: nextPre,
    updatedAt: new Date(),
    revisions: [...(current.revisions ?? []), ...newRevisions],
  })
  return { ...current, pre: nextPre }
}

export const firestoreWritePostAssessment = async ({
  sessionId,
  assessmentId,
  post,
  editedBy = "user",
}) => {
  const { uid } = auth.currentUser
  const ref = assessmentDocRef(uid, sessionId, assessmentId)
  const snap = await getDoc(ref)
  if (!snap.exists()) return null
  const current = snap.data()
  const now = new Date()
  const nextPost = { ...post, recordedAt: post?.recordedAt ?? now }
  const comparison =
    nextPost.status === POST_STATUS_RETESTED
      ? computeComparison({
          pre: current.pre,
          post: nextPost,
          measurementType: current.measurementType,
          testKey: current.testKey,
        })
      : null
  const newRevisions = buildUserRevisions(
    { post: current.post },
    { post: nextPost },
    editedBy
  )
  await updateDoc(ref, {
    post: nextPost,
    comparison,
    updatedAt: now,
    revisions: [...(current.revisions ?? []), ...newRevisions],
  })
  await updateDoc(sessionDocRef(uid, sessionId), {
    hasPostAssessment: true,
  }).catch(() => {})
  return { ...current, post: nextPost, comparison }
}

export const firestoreSetMarkedForRetest = async ({
  sessionId,
  assessmentId,
  markedForRetest,
}) => {
  const { uid } = auth.currentUser
  return updateDoc(assessmentDocRef(uid, sessionId, assessmentId), {
    markedForRetest: !!markedForRetest,
    updatedAt: new Date(),
  })
}

export const firestoreDeleteAssessment = async ({ sessionId, assessmentId }) => {
  const { uid } = auth.currentUser
  return deleteDoc(assessmentDocRef(uid, sessionId, assessmentId))
}

export const firestoreSetSessionOutcome = async ({ sessionId, rating, note }) => {
  const { uid } = auth.currentUser
  return updateDoc(sessionDocRef(uid, sessionId), {
    sessionOutcome: {
      rating: rating ?? null,
      ratedAt: new Date(),
      note: note ?? null,
    },
  }).catch(() => {})
}

export const firestoreSetSessionAssessmentMode = async ({ sessionId, mode }) => {
  const { uid } = auth.currentUser
  return updateDoc(sessionDocRef(uid, sessionId), {
    assessmentMode: mode ?? null,
  }).catch(() => {})
}

/* Commit on session save. Flips drafts to committed and appends to per-client
   outcome timeline. Safe to call with no clientId (no-op for quick sessions). */
export const firestoreCommitSessionAssessments = async ({
  sessionId,
  clientId,
  sessionDate,
}) => {
  const { uid } = auth.currentUser
  const snapshot = await getDocs(assessmentsCollectionRef(uid, sessionId))
  if (snapshot.empty) return { committed: 0 }
  const batch = writeBatch(firestore)
  const timelineUpdates = []
  snapshot.docs.forEach((d) => {
    const data = d.data()
    if (data.isDraft) batch.update(d.ref, { isDraft: false, committedAt: new Date() })
    if (clientId && data.testKey) {
      timelineUpdates.push({
        testKey: data.testKey,
        testLabel: data.testLabel,
        measurementType: data.measurementType,
        entry: {
          assessmentId: data.assessmentId,
          sessionId,
          sessionDate: sessionDate ?? null,
          pre: data.pre ?? null,
          post: data.post ?? null,
          comparison: data.comparison ?? null,
        },
      })
    }
  })
  await batch.commit()
  for (const update of timelineUpdates) {
    const ref = clientOutcomeDocRef(uid, clientId, update.testKey)
    const existing = await getDoc(ref)
    const now = new Date()
    if (!existing.exists()) {
      await setDoc(ref, {
        testKey: update.testKey,
        testLabel: update.testLabel,
        measurementType: update.measurementType,
        timeline: [update.entry],
        firstRecordedAt: now,
        lastRecordedAt: now,
      })
    } else {
      await updateDoc(ref, {
        timeline: arrayUnion(update.entry),
        lastRecordedAt: now,
        testLabel: update.testLabel,
      })
    }
  }
  return { committed: snapshot.size }
}

export const firestoreResolveTestAlias = async ({
  slug,
  label,
  canonicalTestKey,
  conditionNodesId,
  resolvedBy = "user",
}) => {
  const { uid } = auth.currentUser
  return setDoc(testAliasDocRef(uid, slug), {
    slug,
    label,
    canonicalTestKey,
    conditionNodesId: conditionNodesId ?? null,
    resolvedAt: new Date(),
    resolvedBy,
  })
}

export const firestoreGetTestAlias = async (slug) => {
  const { uid } = auth.currentUser
  const snap = await getDoc(testAliasDocRef(uid, slug))
  return snap.exists() ? snap.data() : null
}

export const firestoreSetClientOutcomeTrackingOptIn = async ({ clientId, optedIn }) => {
  const { uid } = auth.currentUser
  return updateDoc(doc(firestore, `users/${uid}/userClients/${clientId}`), {
    outcomeTrackingOptedIn: !!optedIn,
  })
}

export const userSessionAssessmentsOnSnapshot = ({
  uid,
  sessionId,
  setAssessments,
}) => {
  return onSnapshot(
    assessmentsCollectionRef(uid, sessionId),
    (qs) => {
      const data = []
      qs.docs.forEach((d) => data.push(d.data()))
      setAssessments(sortOrder(data, ["createdAt"], ["asc"]))
    },
    (error) => {
      console.log(error)
    }
  )
}

export const userTestAliasesOnSnapshot = ({ uid, setAliases }) => {
  return onSnapshot(
    collection(firestore, `users/${uid}/userTestAliases`),
    (qs) => {
      const map = {}
      qs.docs.forEach((d) => {
        const data = d.data()
        if (data?.slug) map[data.slug] = data
      })
      setAliases(map)
    },
    (error) => {
      console.log(error)
    }
  )
}

/* ── End assessment helpers ─────────────────────────────────────────────── */

export const userDraftTreatmentNotesOnSnapshot = ({ uid, setUserDrafts }) => {
  return onSnapshot(
    query(
      collection(firestore, `users/${uid}/userTreatmentNotes`),
      where("isFinal", "==", false)
    ),
    (query) => {
      let data = []
      query.docs.forEach((doc) =>
        data.push({
          ...doc.data(),
          dateOrder: doc.data()?.lastModified.toDate(),
        })
      )
      setUserDrafts(sortOrder(data, "dateOrder", "desc"))
    },
    (error) => {
      console.log(error)
    }
  )
}
